import {
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tooltip,
} from '@chakra-ui/react';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';
import { useToast } from 'modules/common/components/Toast';
import { MouseEvent, forwardRef, memo, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkBook, convertToArray, readFile } from 'utils/sheet';

import { ButtonCard } from './ButtonCard';
import {
  EMPTY_SHEET_ERROR_TOAST,
  FILE_INPUT_ERROR_TOAST,
  IMPORT_SUCCESS_TOAST,
  ROW_LIMIT_ERROR_TOAST,
  SUPPORTED_FILE_TYPES,
} from './SpreadSheetImport.config';
import { convertToFriendlyFormat } from './SpreadSheetImport.helpers';
import { ImportState, SheetSelectModalContentProps, SpreadSheetImportProps } from './SpreadSheetImport.types';

const SpreadSheetImport = memo(
  forwardRef<HTMLInputElement, SpreadSheetImportProps>(({ icon, isDisabled, onComplete, onError }, ref) => {
    const { t } = useTranslation('common_components', { useSuspense: false, keyPrefix: 'spreadsheet_import' });
    const [importState, setImportState] = useState(ImportState.Default);
    const [workbook, setWorkBook] = useState<WorkBook | null>(null);
    const toast = useToast();

    const triggerButtonTooltipAriaLabel = t('triggerButton.iconTooltipAriaLabel');
    const triggerButtonLabel = t('triggerButton.buttonLabel');
    const triggerButtonDescription = t('triggerButton.description');

    // save the mime type for mixpanel tracking
    const fileMimeType = useRef('');

    // unique id per component for file input
    const fileInputId = useId();

    const handleLoadSheet = (sheetName: string, workbook: WorkBook, fileName: string) => {
      setImportState(ImportState.Default);

      // convert the sheet to a 2D array
      const sheetData = convertToArray(workbook, sheetName);

      // If we don't have data, we had a parsing error, so show the error modal.
      if (!sheetData) {
        onError?.();
        return toast(FILE_INPUT_ERROR_TOAST);
      }

      // Empty sheet
      if (!sheetData.length) {
        onError?.();
        return toast(EMPTY_SHEET_ERROR_TOAST);
      }

      // Same as E1
      // If there are a 1000 lines, it was probably truncated
      // If there are over 1000 rows, we updated this to not load the data, since it may be confusing for the user (but in proper format)
      if (sheetData.length === 1000) {
        onError?.();
        return toast(ROW_LIMIT_ERROR_TOAST);
      }

      // file imported successfully
      toast(IMPORT_SUCCESS_TOAST);
      const fileFormat = convertToFriendlyFormat(fileMimeType.current);
      onComplete(sheetData, fileFormat, fileName);
    };

    const handleImportChange = async (sheetFiles: FileList | null, fileName: string) => {
      if (!sheetFiles || !(sheetFiles.length > 0)) return;
      setImportState(ImportState.Loading);

      // Call the parser to get a 2D array of the data, report an error if the result is rejected from the parser
      let workbook = (await readFile(sheetFiles).catch((e) => console.error(e))) as WorkBook;
      if (!workbook) {
        toast(FILE_INPUT_ERROR_TOAST);
        onError?.();
        return setImportState(ImportState.Default);
      }

      fileMimeType.current = sheetFiles[0].type;
      setWorkBook(workbook);

      // If one sheet, import directly, no sheet selection Modal
      const sheetNames = workbook.SheetNames;
      if (sheetNames.length < 2) return handleLoadSheet(sheetNames[0], workbook, fileName);

      // else select sheet
      return setImportState(ImportState.ShowSheetModal);
    };

    // clear the selected file, so a file with the same name can be selected
    const onClick = (e: MouseEvent<HTMLInputElement>) => {
      if (!e.target) return;
      (e.target as HTMLInputElement).value = '';
    };
    const onCloseModal = () => setImportState(ImportState.Default);
    const onSelectSheet = (sheetName: string) => handleLoadSheet(sheetName, workbook as WorkBook, sheetName);
    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // eslint-disable-next-line no-useless-escape
      const fileName = e.target.value.replace(/.*[\/\\]/, '');
      handleImportChange(e.target.files, fileName);
    };
    const onClickUpload = () => document.getElementById(fileInputId)?.click();

    const showSheetModal = importState === ImportState.ShowSheetModal;
    const isLoading = importState === ImportState.Loading;

    return (
      <>
        <Modal isOpen={showSheetModal} onClose={onCloseModal}>
          <ModalOverlay />
          <SheetSelectModalContent
            onClose={onCloseModal}
            handleLoadSheet={onSelectSheet}
            SheetNames={workbook?.SheetNames ?? []}
          />
        </Modal>
        {!!icon && (
          <Tooltip hasArrow label={triggerButtonTooltipAriaLabel} placement='bottom'>
            <IconButton
              variant={'ghost'}
              size={'sm'}
              isDisabled={isDisabled}
              onClick={onClickUpload}
              icon={<UploadIcon />}
              aria-label={triggerButtonLabel}
            />
          </Tooltip>
        )}
        {!icon && (
          <ButtonCard
            description={triggerButtonDescription}
            buttonLabel={triggerButtonLabel}
            onClick={onClickUpload}
            isLoading={isLoading}
          />
        )}
        <input
          ref={ref}
          data-testid='spreadsheet-import-file-input'
          id={fileInputId}
          style={{ display: 'none' }}
          type='file'
          accept={SUPPORTED_FILE_TYPES.join(', ')}
          onChange={onFileChange}
          onClick={onClick}
        />
      </>
    );
  }),
);
SpreadSheetImport.displayName = 'SpreadSheetImport';

const SheetSelectModalContent = ({ handleLoadSheet, SheetNames = [], onClose }: SheetSelectModalContentProps) => {
  const [selectedSheet, setSelectedSheet] = useState(SheetNames[0]);
  const { t } = useTranslation('common_components', {
    useSuspense: false,
    keyPrefix: 'spreadsheet_import.sheetSelect',
  });

  const cancelButton = t('cancelButtonLabel');
  const uploadButton = t('UploadButtonLabel');
  const modelHeader = t('modalHeader');

  const onSelectSheetName = (e: React.ChangeEvent<HTMLSelectElement>) => setSelectedSheet(e.target.value);
  const onClickUpload = () => handleLoadSheet(selectedSheet);

  return (
    <ModalContent w='sm' maxW='sm' color='font.500'>
      <ModalHeader fontSize='18px'>{modelHeader}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Select size={'sm'} mt={4} value={selectedSheet} onChange={onSelectSheetName}>
          {SheetNames.map((sheetName) => (
            <option key={sheetName} value={sheetName}>
              {sheetName}
            </option>
          ))}
        </Select>
      </ModalBody>
      <ModalFooter>
        <Button variant='ghost' size='sm' onClick={onClose}>
          {cancelButton}
        </Button>
        <Button variant='action' size='sm' onClick={onClickUpload}>
          {uploadButton}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

export default SpreadSheetImport;
